<template>
  <div>
    <ok-cancel-modal
      :title="$t('health.nutritionalPlan.recipesSelector')"
      :ok-button="$t('commons.ok')"
      :cancel-button="$t('commons.cancel')"
      v-on:ok="onOk"
      v-on:cancel="$emit('close')">
      <div slot="body">

        <div class="search-row">
          <search-input
            ref="recipeSearch"
            class="search-input"
            :focus="true"
            v-on:input="onChangeFilter"
          />
          <div class="tag-dropdown">
            <button class="tag-dropdown__toggle" @click="showTagFilter = !showTagFilter">
              <font-awesome-icon :icon="iconTag" />
              <span>{{ showTagFilter ? '▼' : '▲' }}</span>
            </button>
            <div v-if="showTagFilter" class="tag-filters">
      <span
        v-for="tag in allTags"
        :key="tag.id"
        class="tag-filter"
        :class="{ 'tag-filter--active': selectedTags.includes(tag.id) }"
        @click="toggleTag(tag.id)"
      >
        {{ tag.name }}
      </span>
            </div>
          </div>
          <select v-model="selectedType" class="type-filter">
            <option value="">{{ $t('health.nutritionalPlan.allTypes') }}</option>
            <option v-for="type in allTypes" :key="type" :value="type">{{ $t(`health.nutritionalPlan.mealTypes2.${type}`) }}</option>
          </select>
        </div>

        <div class="margin-top-xl recipe-list">
          <h3 class="margin-bottom-l">{{ $t('health.nutritionalPlan.recipes') }}</h3>
          <div
            v-for="(recipe, index) in getFilteredRecipes.slice(0, maxToShow)"
            :key="index"
            class="recipe-item"
            :class="{ 'recipe-item--selected': isChecked(recipe) }"
            @click="onRecipeSelected(recipe)"
          >
            <img
              v-if="recipe.imageUrl"
              :src="recipe.imageUrl"
              :alt="recipe.name"
              class="recipe-item__image"
            />
            <div class="recipe-item__info">
              <span class="recipe-item__type">{{ $t(`health.nutritionalPlan.mealTypes2.${recipe.type}`) }}</span>
              <span class="recipe-item__name">{{ recipe.name }}</span>
              <div class="recipe-item__macros">
                <span>P: {{ totalMacro(recipe.items, 'protein') }}g</span>
                <span>HC: {{ totalMacro(recipe.items, 'carbs') }}g</span>
                <span>G: {{ totalMacro(recipe.items, 'fat') }}g</span>
                <span>Kcal: {{ totalMacro(recipe.items, 'calories') }}</span>
              </div>
              <div class="recipe-item__tags">
                <span v-for="tag in recipe.tags" :key="tag.id" class="recipe-item__tag">
                  {{ tag.name }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="getFilteredRecipes.length > maxToShow" class="and-x-more">
            {{ $t('variables.andXMore', { count: getFilteredRecipes.length - maxToShow }) }}
          </div>
        </div>

      </div>
    </ok-cancel-modal>
  </div>
</template>

<script>
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import SearchInput from '@/components/shared/SearchInput';
import { faTag } from '@fortawesome/pro-regular-svg-icons';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';

export default {
  name: 'SearchRecipeModal',
  components: {
    OkCancelModal,
    SearchInput,
    FontAwesomeIcon
  },
  props: {
    recipes: {
      type: Array,
      default: () => []
    },
    value: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      recipeFilter: '',
      selectedRecipe: this.value,
      maxToShow: 50,
      selectedTags: [],
      showTagFilter: false,
      iconTag: faTag,
      selectedType: ''

    };
  },
  computed: {
    allTypes() {
      return [...new Set(this.recipes.map(r => r.type).filter(Boolean))];
    },
    allTags() {
      const tagsMap = {};
      this.recipes.forEach(recipe => {
        (recipe.tags || []).forEach(tag => {
          tagsMap[tag.id] = tag;
        });
      });
      return Object.values(tagsMap);
    },
    getFilteredRecipes() {
      let result = this.recipes;

      if (this.recipeFilter !== '') {
        result = result.filter(recipe =>
          replaceSpecialChars(recipe.name.toUpperCase())
            .includes(replaceSpecialChars(this.recipeFilter.toUpperCase()))
        );
      }

      if (this.selectedTags.length > 0) {
        result = result.filter(recipe =>
          this.selectedTags.every(tagId =>
            (recipe.tags || []).some(tag => tag.id === tagId)
          )
        );
      }

      if (this.selectedType !== '') {
        result = result.filter(recipe => recipe.type === this.selectedType);
      }

      return result;
    }
  },
  methods: {
    isChecked(recipe) {
      return this.selectedRecipe && this.selectedRecipe.id === recipe.id;
    },
    onRecipeSelected(recipe) {
      this.selectedRecipe = recipe;
    },
    onOk() {
      if (this.selectedRecipe) {
        this.$emit('input', this.selectedRecipe);
      }
      this.$emit('close');
    },
    onChangeFilter(filter) {
      this.recipeFilter = filter;
    },
    totalMacro(items, macro) {
      if (!items) return 0;
      return items.reduce((sum, item) => sum + (item[macro] || 0), 0).toFixed(1);
    },
    toggleTag(tagId) {
      const index = this.selectedTags.indexOf(tagId);
      if (index === -1) {
        this.selectedTags.push(tagId);
      } else {
        this.selectedTags.splice(index, 1);
      }
    }
  }
};

const replaceSpecialChars = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/([^\w]+|\s+)/g, '-')
    .replace(/(^-+|-+$)/g, '');
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.recipe-list {
  min-height: calc(100vh / 2);
  max-height: calc(100vh / 2);
  overflow-y: auto;
}

.search-input {
  margin-bottom: 1em;
}

.recipe-item {
  display: flex;
  flex-direction: row;
  border: 1px solid $black-light6;
  border-radius: $radius;
  margin-bottom: 0.75em;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: rgba($primary, 0.05);
    border-color: $primary;
  }

  &--selected {
    border: 2px solid $primary;
    background: rgba($primary, 0.1);
  }

  &__image {
    width: 50%;
    height: 140px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__info {
    width: 50%;
    padding: 0.75em;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
  }

  &__type {
    font-size: 0.8em;
    color: $black-light5;
  }

  &__name {
    font-weight: bold;
    font-size: 0.95em;
  }

  &__macros {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
    font-size: 0.78em;
    color: $black-light4;
    margin-top: auto;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25em;
  }

  &__tag {
    background: rgba($primary, 0.1);
    color: $primary;
    border-radius: 12px;
    padding: 0.1em 0.5em;
    font-size: 0.75em;
  }
}

.and-x-more {
  color: $black-light5;
  text-align: center;
  padding: 0.5em;
}

.search-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  position: relative;

  .search-input {
    flex: 1;
    margin-bottom: 0;
  }
}

.tag-dropdown {
  position: relative;

  &__toggle {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.5em 0.75em;
    background: $black-light8;
    border: 1px solid $black-light6;
    border-radius: $radius;
    cursor: pointer;
    white-space: nowrap;

    &:hover { border-color: $primary; }
  }
}

.tag-filters {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
  padding: 0.75em;
  background: $white;
  border: 1px solid $black-light6;
  border-radius: $radius;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba($black, 0.1);
}

.tag-filter {
  background: $black-light8;
  border: 1px solid $black-light6;
  border-radius: 12px;
  padding: 0.2em 0.8em;
  font-size: 0.8em;
  cursor: pointer;

  &--active {
    background: $primary;
    color: $white;
    border-color: $primary;
  }

  &:hover { border-color: $primary; }
}
.type-filter {
  padding: 0.5em 0.75em;
  border: 1px solid $black-light6;
  border-radius: $radius;
  background: $black-light8;
  cursor: pointer;
  font-size: 0.9em;
  white-space: nowrap;

  &:hover { border-color: $primary; }
  &:focus { outline: none; border-color: $primary; }
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/nutritionPlan/AddRecipeModal.vue